/**
 * React Query hooks for Tools Marketplace
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getToolCategories,
  getCategoryWithTools,
  getToolVendors,
  getVendorWithTools,
  getTools,
  trackToolView,
  getToolReviews,
  getMyReview,
  createReview,
  updateMyReview,
  deleteMyReview,
  getRatingSummary,
} from "@/services/tools";
import type {
  ToolCategory,
  ToolVendor,
  ToolListResponse,
  ToolQueryParams,
  CategoryWithToolsResponse,
  VendorWithToolsResponse,
  ReviewListResponse,
  MyReview,
  RatingSummary,
  ReviewCreateRequest,
  ReviewUpdateRequest,
} from "@/types/tools";

// ============================================================================
// Query Keys
// ============================================================================

export const toolsKeys = {
  all: ["tools"] as const,
  categories: () => [...toolsKeys.all, "categories"] as const,
  category: (slug: string) => [...toolsKeys.categories(), slug] as const,
  vendors: (verifiedOnly?: boolean) =>
    [...toolsKeys.all, "vendors", { verifiedOnly }] as const,
  vendor: (slug: string) => [...toolsKeys.all, "vendor", slug] as const,
  list: (params?: ToolQueryParams) =>
    [...toolsKeys.all, "list", params] as const,
  // Review keys
  reviews: (toolId: string) => [...toolsKeys.all, toolId, "reviews"] as const,
  reviewsPaged: (toolId: string, page: number) =>
    [...toolsKeys.reviews(toolId), { page }] as const,
  myReview: (toolId: string) => [...toolsKeys.all, toolId, "myReview"] as const,
  ratingSummary: (toolId: string) =>
    [...toolsKeys.all, toolId, "ratingSummary"] as const,
};

// ============================================================================
// Category Hooks
// ============================================================================

/**
 * Fetch all tool categories with tool counts
 */
export function useToolCategories() {
  return useQuery<ToolCategory[], Error>({
    queryKey: toolsKeys.categories(),
    queryFn: getToolCategories,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch a category with its tools
 */
export function useCategoryWithTools(
  slug: string,
  skip = 0,
  limit = 20,
  enabled = true,
) {
  return useQuery<CategoryWithToolsResponse, Error>({
    queryKey: toolsKeys.category(slug),
    queryFn: () => getCategoryWithTools(slug, skip, limit),
    enabled: enabled && !!slug,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// ============================================================================
// Vendor Hooks
// ============================================================================

/**
 * Fetch all tool vendors with tool counts
 */
export function useToolVendors(verifiedOnly = false) {
  return useQuery<ToolVendor[], Error>({
    queryKey: toolsKeys.vendors(verifiedOnly),
    queryFn: () => getToolVendors(verifiedOnly),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch a vendor with their tools
 */
export function useVendorWithTools(
  slug: string,
  skip = 0,
  limit = 20,
  enabled = true,
) {
  return useQuery<VendorWithToolsResponse, Error>({
    queryKey: toolsKeys.vendor(slug),
    queryFn: () => getVendorWithTools(slug, skip, limit),
    enabled: enabled && !!slug,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// ============================================================================
// Tools List Hook
// ============================================================================

/**
 * Fetch tools with filters and pagination
 */
export function useToolsList(params?: ToolQueryParams) {
  return useQuery<ToolListResponse, Error>({
    queryKey: toolsKeys.list(params),
    queryFn: () => getTools(params),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

// ============================================================================
// View Tracking Hook
// ============================================================================

/**
 * Track a tool view (mutation)
 */
export function useTrackToolView() {
  return useMutation({
    mutationFn: (toolId: string) => trackToolView(toolId),
  });
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Get category by slug from cached categories
 */
export function useCategoryBySlug(slug: string) {
  const { data: categories } = useToolCategories();
  return categories?.find((c) => c.slug === slug);
}

/**
 * Get vendor by slug from cached vendors
 */
export function useVendorBySlug(slug: string) {
  const { data: vendors } = useToolVendors();
  return vendors?.find((v) => v.slug === slug);
}

// ============================================================================
// Review Hooks
// ============================================================================

/**
 * Fetch approved reviews for a tool
 */
export function useToolReviews(toolId: string, page = 1, pageSize = 10) {
  return useQuery<ReviewListResponse, Error>({
    queryKey: toolsKeys.reviewsPaged(toolId, page),
    queryFn: () => getToolReviews(toolId, page, pageSize),
    enabled: !!toolId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Fetch current user's review for a tool
 */
export function useMyReview(toolId: string) {
  return useQuery<MyReview | null, Error>({
    queryKey: toolsKeys.myReview(toolId),
    queryFn: () => getMyReview(toolId),
    enabled: !!toolId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Fetch rating summary for a tool
 */
export function useRatingSummary(toolId: string) {
  return useQuery<RatingSummary, Error>({
    queryKey: toolsKeys.ratingSummary(toolId),
    queryFn: () => getRatingSummary(toolId),
    enabled: !!toolId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Create a new review for a tool
 */
export function useCreateReview(toolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReviewCreateRequest) => createReview(toolId, data),
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: toolsKeys.myReview(toolId) });
      queryClient.invalidateQueries({ queryKey: toolsKeys.reviews(toolId) });
      queryClient.invalidateQueries({
        queryKey: toolsKeys.ratingSummary(toolId),
      });
    },
  });
}

/**
 * Update current user's review for a tool
 */
export function useUpdateReview(toolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReviewUpdateRequest) => updateMyReview(toolId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: toolsKeys.myReview(toolId) });
      queryClient.invalidateQueries({ queryKey: toolsKeys.reviews(toolId) });
      queryClient.invalidateQueries({
        queryKey: toolsKeys.ratingSummary(toolId),
      });
    },
  });
}

/**
 * Delete current user's review for a tool
 */
export function useDeleteReview(toolId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteMyReview(toolId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: toolsKeys.myReview(toolId) });
      queryClient.invalidateQueries({ queryKey: toolsKeys.reviews(toolId) });
      queryClient.invalidateQueries({
        queryKey: toolsKeys.ratingSummary(toolId),
      });
    },
  });
}
